import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllOrders, getAllUsers, updateOrderStatus, getCurrentUser, getOrderById } from '../api';
import AlertModal, { showSuccess, showError } from './AlertModal';
import {
  ShoppingBagIcon, ClockIcon, CheckCircleIcon, CurrencyRupeeIcon,
  UsersIcon, ArrowLeftIcon, ArrowPathIcon, ChevronDownIcon, ChevronUpIcon,
  MapPinIcon, UserIcon, CreditCardIcon, DocumentTextIcon, ShoppingCartIcon,
  ExclamationTriangleIcon, InboxIcon, XCircleIcon, PhoneIcon, EnvelopeIcon,
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  pending:          { label: 'Pending',         color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
  confirmed:        { label: 'Confirmed',        color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
  processing:       { label: 'Processing',       color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
  out_for_delivery: { label: 'Out for Delivery', color: '#ea580c', bg: '#ffedd5', border: '#fdba74' },
  delivered:        { label: 'Delivered',        color: '#16a34a', bg: '#dcfce7', border: '#86efac' },
  cancelled:        { label: 'Cancelled',        color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};
const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const ROLE_CONFIG = {
  customer: { label: 'Customer', color: '#2563eb', bg: '#dbeafe' },
  b2b:      { label: 'B2B',      color: '#7c3aed', bg: '#ede9fe' },
  admin:    { label: 'Admin',    color: '#dc2626', bg: '#fee2e2' },
};

function Badge({ text, color, bg, border }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em',
      color, background: bg, border: `1px solid ${border || color + '44'}`,
      textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{text}</span>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div style={{
      background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '14px',
      padding: '1.2rem 1.4rem', display: 'flex', alignItems: 'center', gap: '1rem',
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s',
    }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 16px ${accent}33`}
    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'}
    >
      <div style={{
        width: 48, height: 48, borderRadius: '12px',
        background: accent + '18', border: `1.5px solid ${accent}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon style={{ width: 24, height: 24, color: accent }} />
      </div>
      <div>
        <div style={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
        <div style={{ color: '#111827', fontSize: '1.55rem', fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [navigate]);

  const loadOrders = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await getAllOrders();
      if (res.success) setOrders(res.orders || []);
      else setError(res.message || 'Failed to load orders.');
    } catch (e) { setError('Network error.'); }
    finally { setLoading(false); }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await getAllUsers();
      if (res.success) setUsers(res.users || []);
      else setError(res.message || 'Failed to load users.');
    } catch (e) { setError('Network error.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'orders') loadOrders(); else loadUsers();
  }, [tab, loadOrders, loadUsers]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleToggleExpand = async (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
      return;
    }
    setExpandedOrder(orderId);
    if (orderDetails[orderId]) return;
    setDetailLoading(orderId);
    try {
      const res = await getOrderById(orderId);
      if (res.success && res.order) setOrderDetails(prev => ({ ...prev, [orderId]: res.order }));
    } catch { }
    finally { setDetailLoading(null); }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      const res = await updateOrderStatus({ order_id: orderId, status: newStatus });
      if (res.success) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
        showToast(`Status updated to "${STATUS_CONFIG[newStatus]?.label}"`);
      } else showToast(res.message || 'Update failed.', 'error');
    } catch { showToast('Network error.', 'error'); }
    finally { setUpdatingId(null); }
  };

  const filteredOrders = orders.filter(o => {
    const ms = statusFilter === 'all' || o.status === statusFilter;
    const q = searchQ.toLowerCase();
    const mq = !q || o.order_number?.toLowerCase().includes(q) || o.full_name?.toLowerCase().includes(q) || o.email?.toLowerCase().includes(q) || o.phone?.includes(q);
    return ms && mq;
  });

  const filteredUsers = users.filter(u => {
    const q = searchQ.toLowerCase();
    return !q || u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.includes(q);
  });

  const stats = {
    total:    orders.length,
    pending:  orders.filter(o => o.status === 'pending').length,
    delivered:orders.filter(o => o.status === 'delivered').length,
    revenue:  orders.filter(o => o.status !== 'cancelled').reduce((s, o) => s + parseFloat(o.total_amount || 0), 0),
  };

  const currentUser = getCurrentUser();

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: "'Inter', sans-serif", color: '#111827' }}>
      <div style={{ background: '#16a34a', padding: '7px 0', color: 'white' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', padding: '0 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <span style={{display:'flex',alignItems:'center',gap:4}}><ClockIcon style={{width:13,height:13}}/> Mon-Sat: 7AM - 10PM</span>
            <span style={{display:'flex',alignItems:'center',gap:4}}><PhoneIcon style={{width:13,height:13}}/> +91 98765 43210</span>
            <span style={{display:'flex',alignItems:'center',gap:4}}><EnvelopeIcon style={{width:13,height:13}}/> kilomart685@gmail.com</span>
          </div>
          <span style={{display:'flex',alignItems:'center',gap:4}}><UserIcon style={{width:13,height:13}}/> Welcome, {currentUser?.full_name || currentUser?.email}</span>
        </div>
      </div>

      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 0', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', padding: '0 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/favicon.svg" alt="" width={28} height={28} style={{ display: 'block', borderRadius: 6 }} />
            <span style={{ fontWeight: 800, fontSize: '1.2rem', color: '#16a34a', letterSpacing: '-0.5px' }}>ePayhub <span style={{ color: '#1f2937' }}>Store</span></span>
            <span style={{ marginLeft: 6, background: '#16a34a', color: '#fff', fontSize: '0.65rem', fontWeight: 800, padding: '2px 8px', borderRadius: '20px' }}>ADMIN</span>
          </div>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 16px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
            <ArrowLeftIcon style={{width:15,height:15}}/> Back to Store
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1300, margin: '0 auto', padding: '1.8rem 1.5rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Order Control Panel</h1>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1rem', marginBottom: '1.8rem' }}>
          <StatCard icon={ShoppingBagIcon} label="Total Orders" value={stats.total} accent="#16a34a" />
          <StatCard icon={ClockIcon} label="Pending" value={stats.pending} accent="#d97706" />
          <StatCard icon={CheckCircleIcon} label="Delivered" value={stats.delivered} accent="#16a34a" />
          <StatCard icon={CurrencyRupeeIcon} label="Revenue" value={`₹${stats.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} accent="#7c3aed" />
          <StatCard icon={UsersIcon} label="Total Users" value={users.length || 0} accent="#2563eb" />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.2rem' }}>
          {[['orders', <><ShoppingCartIcon style={{ width: 16, height: 16, marginRight: 6 }} /> Orders</>], ['users', <><UsersIcon style={{ width: 16, height: 16, marginRight: 6 }} /> Users</>]].map(([key, label]) => (
            <button key={key}
              onClick={() => { setTab(key); setSearchQ(''); setStatusFilter('all'); }}
              style={{
                padding: '9px 22px', borderRadius: '8px', border: `1.5px solid ${tab === key ? '#16a34a' : '#e5e7eb'}`,
                background: tab === key ? '#16a34a' : '#fff', color: tab === key ? '#fff' : '#374151',
                fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
              }}
            >{label}</button>
          ))}
        </div>

        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '1rem 1.2rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
            <input
              style={{ flex: 1, minWidth: 220, padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '0.875rem' }}
              placeholder="Search..." value={searchQ} onChange={e => setSearchQ(e.target.value)}
            />
            {tab === 'orders' && (
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #e5e7eb' }}>
                <option value="all">All Statuses</option>
                {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
            )}
            <button onClick={tab === 'orders' ? loadOrders : loadUsers} style={{ display:'flex',alignItems:'center',gap:6,padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
              <ArrowPathIcon style={{ width: 16, height: 16 }} /> Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '4rem', textAlign: 'center' }}>
              <ArrowPathIcon style={{ width: 36, height: 36, margin: '0 auto 0.5rem', display:'block', animation:'spin 1s linear infinite' }} />
              Loading…
            </div>
          ) : error ? (
            <div style={{ padding: '4rem', textAlign: 'center', color: '#ef4444' }}>
              <ExclamationTriangleIcon style={{ width: 36, height: 36, margin: '0 auto 0.5rem', display:'block' }} />
              {error}
            </div>
          ) : tab === 'orders' ? (
            <div style={{ overflowX: 'auto' }}>
              {filteredOrders.length === 0 ? (
                <div style={{ padding: '4rem', textAlign: 'center' }}>
                  <InboxIcon style={{ width: 36, height: 36, margin: '0 auto 0.5rem', display:'block' }} />
                  No orders found.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Order #', 'Customer', 'Phone', 'Amount', 'Status', 'Change', 'Action'].map(h => (
                        <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', borderBottom: '1.5px solid #e5e7eb' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map(order => {
                      const cfg = STATUS_CONFIG[order.status] || { label: order.status, color: '#6b7280', bg: '#f3f4f6' };
                      const isExp = expandedOrder === order.id;
                      return (
                        <React.Fragment key={order.id}>
                          <tr>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6', fontWeight: 700, fontSize: '0.8rem' }}>{order.order_number}</td>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>{order.full_name}</td>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>{order.phone || '—'}</td>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6', fontWeight: 700 }}>₹{parseFloat(order.total_amount || 0).toLocaleString('en-IN')}</td>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}><Badge text={cfg.label} color={cfg.color} bg={cfg.bg} border={cfg.border} /></td>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>
                              <select value={order.status} disabled={updatingId === order.id} onChange={e => handleStatusChange(order.id, e.target.value)}>
                                {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>
                              <button onClick={() => handleToggleExpand(order.id)} style={{ display:'flex',alignItems:'center',gap:4, background: 'none', border: '1px solid #16a34a', borderRadius: '4px', color: '#16a34a', cursor: 'pointer', padding: '2px 8px' }}>
                                {isExp ? <><ChevronUpIcon style={{width:12,height:12}}/> Hide</> : <><ChevronDownIcon style={{width:12,height:12}}/> Info</>}
                              </button>
                            </td>
                          </tr>
                          {isExp && (
                            <tr style={{ background: '#f0fdf4' }}>
                              <td colSpan={7} style={{ padding: '1rem 1.2rem' }}>
                                {detailLoading === order.id ? (
                                  <div style={{ display:'flex',alignItems:'center',gap:8, color:'#6b7280' }}>
                                    <ArrowPathIcon style={{ width:16,height:16,animation:'spin 1s linear infinite' }}/> Loading order details...
                                  </div>
                                ) : orderDetails[order.id] ? (() => {
                                  const d = orderDetails[order.id];
                                  const items = d.items || [];
                                  return (
                                    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                                      {/* Customer & Address Info */}
                                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem 2rem', fontSize:'0.82rem', color:'#374151' }}>
                                        <div style={{ display:'flex',alignItems:'center',gap:6 }}><UserIcon style={{width:14,height:14,color:'#6b7280',flexShrink:0}}/> <strong>Customer:</strong> {d.full_name}</div>
                                        <div style={{ display:'flex',alignItems:'center',gap:6 }}><PhoneIcon style={{width:14,height:14,color:'#6b7280',flexShrink:0}}/> <strong>Phone:</strong> {d.phone || '—'}</div>
                                        <div style={{ display:'flex',alignItems:'center',gap:6 }}><EnvelopeIcon style={{width:14,height:14,color:'#6b7280',flexShrink:0}}/> <strong>Email:</strong> {d.email || '—'}</div>
                                        <div style={{ display:'flex',alignItems:'center',gap:6 }}><CreditCardIcon style={{width:14,height:14,color:'#6b7280',flexShrink:0}}/> <strong>Payment:</strong> {(d.payment_method || '').toUpperCase()}</div>
                                        <div style={{ display:'flex',alignItems:'flex-start',gap:6, gridColumn:'1/-1' }}><MapPinIcon style={{width:14,height:14,color:'#6b7280',flexShrink:0,marginTop:2}}/> <strong>Address:</strong> {d.address}{d.city ? `, ${d.city}` : ''}{d.pincode ? ` - ${d.pincode}` : ''}{d.delivery_area ? ` (${d.delivery_area})` : ''}</div>
                                      </div>

                                      {/* Order Summary */}
                                      <div style={{ display:'flex', gap:'1.5rem', fontSize:'0.8rem', color:'#6b7280', borderTop:'1px solid #bbf7d0', paddingTop:'0.5rem' }}>
                                        <span><strong>Subtotal:</strong> ₹{parseFloat(d.subtotal || 0).toLocaleString('en-IN')}</span>
                                        <span><strong>Delivery:</strong> {parseFloat(d.delivery_fee || 0) > 0 ? `₹${parseFloat(d.delivery_fee).toLocaleString('en-IN')}` : 'FREE'}</span>
                                        <span><strong>Total:</strong> ₹{parseFloat(d.total_amount || 0).toLocaleString('en-IN')}</span>
                                        <span><strong>Date:</strong> {d.created_at ? new Date(d.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}</span>
                                        {d.notes && <span><strong>Notes:</strong> {d.notes}</span>}
                                      </div>

                                      {/* Items Table */}
                                      {items.length > 0 && (
                                        <div style={{ borderTop:'1px solid #bbf7d0', paddingTop:'0.5rem' }}>
                                          <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#15803d', marginBottom:'0.4rem', display:'flex',alignItems:'center',gap:5 }}>
                                            <DocumentTextIcon style={{width:14,height:14}}/> Order Items ({items.length})
                                          </div>
                                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                                            <thead>
                                              <tr style={{ background:'#dcfce7' }}>
                                                <th style={{ padding:'6px 10px', textAlign:'left', fontWeight:700, color:'#166534', borderBottom:'1px solid #bbf7d0' }}>#</th>
                                                <th style={{ padding:'6px 10px', textAlign:'left', fontWeight:700, color:'#166534', borderBottom:'1px solid #bbf7d0' }}>Product</th>
                                                <th style={{ padding:'6px 10px', textAlign:'left', fontWeight:700, color:'#166534', borderBottom:'1px solid #bbf7d0' }}>Category</th>
                                                <th style={{ padding:'6px 10px', textAlign:'center', fontWeight:700, color:'#166534', borderBottom:'1px solid #bbf7d0' }}>Qty</th>
                                                <th style={{ padding:'6px 10px', textAlign:'right', fontWeight:700, color:'#166534', borderBottom:'1px solid #bbf7d0' }}>Price</th>
                                                <th style={{ padding:'6px 10px', textAlign:'right', fontWeight:700, color:'#166534', borderBottom:'1px solid #bbf7d0' }}>Subtotal</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {items.map((item, idx) => (
                                                <tr key={idx} style={{ borderBottom:'1px solid #e5e7eb' }}>
                                                  <td style={{ padding:'6px 10px', color:'#6b7280' }}>{idx + 1}</td>
                                                  <td style={{ padding:'6px 10px', fontWeight:600, color:'#111827' }}>{item.name}</td>
                                                  <td style={{ padding:'6px 10px', color:'#6b7280' }}>{item.category || '—'}</td>
                                                  <td style={{ padding:'6px 10px', textAlign:'center', fontWeight:600 }}>{item.quantity}</td>
                                                  <td style={{ padding:'6px 10px', textAlign:'right' }}>₹{parseFloat(item.price || 0).toFixed(2)}</td>
                                                  <td style={{ padding:'6px 10px', textAlign:'right', fontWeight:700, color:'#15803d' }}>₹{parseFloat(item.subtotal || 0).toFixed(2)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })() : (
                                  <div style={{ color:'#ef4444', display:'flex',alignItems:'center',gap:6 }}>
                                    <ExclamationTriangleIcon style={{width:16,height:16}}/> Error loading order details.
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {filteredUsers.length === 0 ? (
                <div style={{ padding: '4rem', textAlign: 'center' }}>
                  <UsersIcon style={{ width: 36, height: 36, margin: '0 auto 0.5rem', display:'block' }} />
                  No users found.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Name', 'Email', 'Role', 'Verified'].map(h => (
                        <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', borderBottom: '1.5px solid #e5e7eb' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>{user.full_name}</td>
                        <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>{user.email}</td>
                        <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}><Badge text={ROLE_CONFIG[user.role]?.label || user.role} color={ROLE_CONFIG[user.role]?.color} bg={ROLE_CONFIG[user.role]?.bg} /></td>
                        <td style={{ padding: '13px 14px', borderBottom: '1px solid #f3f4f6' }}>
                          {user.is_verified ? <span style={{ color: '#16a34a', display:'flex',alignItems:'center',gap:3 }}><CheckCircleIcon style={{width:15,height:15}}/> Yes</span> : <span style={{ color: '#9ca3af', display:'flex',alignItems:'center',gap:3 }}><XCircleIcon style={{width:15,height:15}}/> No</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '13px 20px', borderRadius: '10px', background: toast.type === 'error' ? '#dc2626' : '#16a34a', color: '#fff', display:'flex',alignItems:'center',gap:8 }}>
          {toast.type === 'error' ? <XCircleIcon style={{width:18,height:18}}/> : <CheckCircleIcon style={{width:18,height:18}}/>}
          {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
}
